import { mergeRefs } from '@chakra-ui/react-utils';
import React, { useMemo } from 'react';

import { Box, type BoxProps } from '@frontend/legacy-components';

import { CASCADER_SUBCOMPONENT_NAMES } from '../consts';
import { useCascaderState, useCascaderComponentsContext } from '../context';
import type { CascaderOption, CascaderCommonProps } from '../types';

export interface MenuWrapperProps<OptionType extends CascaderOption>
  extends BoxProps {
  /** Ref to the full menu surface (outside-click + focus guard). */
  menuRef?: React.Ref<HTMLDivElement>;
  /**
   * Ref to the horizontally scrollable branch row. Defaults to the same node as {@link menuRef}
   * when omitted (standard single-shell menu).
   */
  scrollRef?: React.Ref<HTMLDivElement>;
  children: React.ReactNode;
  getCommonProps: () => CascaderCommonProps<OptionType>;
}

export const InternalMenuWrapper = <OptionType extends CascaderOption>({
  menuRef,
  scrollRef,
  children,
}: {
  menuRef?: React.Ref<HTMLDivElement>;
  scrollRef?: React.Ref<HTMLDivElement>;
  /** The children to be rendered. */
  children: React.ReactNode;
}) => {
  const { cascaderStyles, getCommonProps } = useCascaderState<OptionType>();
  const { getComponent } = useCascaderComponentsContext<OptionType>();
  const CustomMenuWrapper = useMemo(
    () => getComponent(CASCADER_SUBCOMPONENT_NAMES.MENU_WRAPPER),
    [getComponent],
  );

  return (
    <CustomMenuWrapper
      menuRef={menuRef}
      scrollRef={scrollRef}
      getCommonProps={getCommonProps}
      __css={cascaderStyles.menuWrapper}
      className="cascader-menu-wrapper"
    >
      {children}
    </CustomMenuWrapper>
  );
};

export const MenuWrapper = <OptionType extends CascaderOption>({
  menuRef,
  scrollRef,
  children,
  getCommonProps: _,
  ...rest
}: MenuWrapperProps<OptionType>) => {
  return (
    <Box ref={mergeRefs(menuRef, scrollRef)} {...rest}>
      {children}
    </Box>
  );
};
